import { useEffect } from 'react';
import { useRouter } from 'expo-router';
import {
  getOnboardingRouteForStep,
  isOnboardingComplete,
  ONBOARDING_TRIAL_ACTIVATION_ROUTE,
} from '@perdiem/trpc/onboarding';
import { trpc } from '../../lib/trpc';
import { ErrorState, LoadingSpinner } from '../../components';

const IS_LOCAL_DEV_BYPASS = process.env['EXPO_PUBLIC_DEV_BYPASS'] === 'true';

/**
 * Onboarding entry point — reads current step and redirects to the right step screen.
 */
export default function OnboardingIndex() {
  const router = useRouter();
  const householdQ = trpc.households.get.useQuery();
  const subscriptionQ = trpc.subscription.getStatus.useQuery();

  useEffect(() => {
    if (
      householdQ.isLoading
      || subscriptionQ.isLoading
      || householdQ.error
      || subscriptionQ.error
      || !householdQ.data
    ) return;
    const step = householdQ.data.onboardingStep;
    if (isOnboardingComplete(step)) {
      router.replace('/(app)');
      return;
    }
    if (
      !IS_LOCAL_DEV_BYPASS
      && !householdQ.data.isDemo
      && !subscriptionQ.data?.isActive
      && step > 1
    ) {
      router.replace(ONBOARDING_TRIAL_ACTIVATION_ROUTE);
      return;
    }
    const route = getOnboardingRouteForStep(step);
    router.replace(route as any);
  }, [
    householdQ.data,
    householdQ.error,
    householdQ.isLoading,
    router,
    subscriptionQ.data,
    subscriptionQ.error,
    subscriptionQ.isLoading,
  ]);

  if (householdQ.isLoading || subscriptionQ.isLoading) {
    return <LoadingSpinner message="Loading your progress..." />;
  }

  if (householdQ.error || subscriptionQ.error) {
    return (
      <ErrorState
        message="Could not load your onboarding access."
        error={householdQ.error ?? subscriptionQ.error}
        screen="onboarding-router"
        metadata={{
          failedQueries: householdQ.error ? 'households.get' : 'subscription.getStatus',
        }}
        onRetry={() => {
          void Promise.all([householdQ.refetch(), subscriptionQ.refetch()]);
        }}
      />
    );
  }

  if (!householdQ.data) {
    return (
      <ErrorState
        message="We couldn't find your onboarding progress."
        screen="onboarding-router"
        metadata={{ failedQueries: 'households.get', householdMissing: true }}
        onRetry={() => {
          void householdQ.refetch();
        }}
      />
    );
  }

  return <LoadingSpinner message="Loading your progress..." />;
}
